<script setup lang="ts">
// validation
import { defineRule, useField, useForm } from 'vee-validate';
import { email, required } from '@vee-validate/rules';

// validation rules
defineRule('email', email);
defineRule('required', required);

definePageMeta({
  layout: 'simple',
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const userService = useUserService();
const userStore = useUserStore();
const { $gettext, $pgettext, interpolate } = useGettext();
const { locale } = useI18n();
const { i18nMarketingSignupLabel, i18nTermsOfUseLink } = useI18nMeta();

/********************
 * REFS & VARS       *
 ********************/
const loading = ref(false);
const error = ref(null);
const success = ref(null);

// form
const { validate } = useForm({
  validationSchema: {
    email: 'required|email',
    password: 'required',
    marketingSignup: '',
  },
});

const { value: emailValue, errorMessage: emailError } = useField<string>('email');
const { value: passwordValue, errorMessage: passwordError } = useField<string>('password');
const { value: marketingSignupValue } = useField<boolean>('marketingSignup');

const i18nGetPasswordLabel = computed(() => {
  return $gettext('Password');
});

const i18nGetEmailLabel = computed(() => {
  return $gettext('E-Mail');
});

const i18nGetRegisterButtonLabel = computed(() => {
  return loading.value ? $gettext('Please wait…') : $pgettext('Account form button', 'Create account');
});

const i18nGetSignInText = computed(() => {
  return $gettext('Sign in instead!');
});

const i18PrivacyPolicyLink = computed(() => {
  const text = $pgettext('Terms of Use Link Text', 'privacy policy');
  const link = $pgettext('Terms of Use Link URL', 'https://en.camping.info/privacy');
  return `<a href="${link}" class="whitespace-nowrap" target="_blank" rel="noopener">${text}</a>`;
});

const finished = computed(() => {
  return success.value !== null;
});

/********************
 * FUNCTIONS         *
 ********************/

async function submit() {
  const { valid } = await validate();
  if (!valid) {
    return false;
  }

  success.value = null;
  error.value = null;
  loading.value = true;

  // we need to use the window.location, since vue-router has no option to get the origin
  userService
    .register(locale.value, { email: emailValue.value, password: passwordValue.value, marketing_signup: marketingSignupValue.value, insider_uid: userStore.insiderUid || null })
    .then(async (res: any) => {
      if (res !== null) {
        const successMsg = $gettext(
          'Your account has been created! Please activate your account using the instructions we just sent to you (%{email}).',
        );
        success.value = interpolate(successMsg, { email: emailValue.value });

        // user will be logged in by default after registering a new account (1 month period)
        await userStore.saveToken({
          token: res.key,
          persistence: false,
        });

        userStore.init(locale.value);

        loading.value = false;
      }
    })
    .catch((err) => {
      loading.value = false;
      if (err?.response?._data) {
        error.value = err.response._data;
      }
    });

  return true;
}

function finish() {
  if (success.value !== null) {
    return navigateTo(localePath({ name: 'index', query: route.query }));
  }
}

function onMarketingSignupClicked(payload: boolean) {
  marketingSignupValue.value = payload;
}
</script>

<template>
  <client-only>
    <section class="mx-auto w-full max-w-[480px] px-4 py-0">
      <transition
        name="slide-fade"
        appear
      >
        <div>
          <h1 class="mb-6 text-xl">
            {{ $pgettext('Account form title', 'Create a new account') }}
          </h1>

          <CiSocialLogin />

          <form @submit.prevent="submit">
            <InputTextfield
              id="email"
              v-model="emailValue"
              :label="i18nGetEmailLabel"
              :class="{
                hidden: finished,
              }"
              :error="!!emailError"
              type="email"
              inverted
              name="email"
            />

            <InputTextfield
              id="password"
              v-model="passwordValue"
              :label="i18nGetPasswordLabel"
              :class="{
                hidden: finished,
              }"
              :error="!!passwordError"
              :password-toggle="true"
              name="password"
              type="password"
              autocomplete="on"
              inverted
            >
              <CiPasswordMeter
                class="absolute right-0 top-[7px]"
                :text="passwordValue"
              />
            </InputTextfield>

            <InputCheckbox
              id="marketingSignup"
              :allow-html="true"
              :label="i18nMarketingSignupLabel"
              :class="{
                hidden: finished,
              }"
              :value="marketingSignupValue"
              name="marketingSignup"
              @input="onMarketingSignupClicked"
            />

            <CiInfoPanel
              :condition="error !== null"
              type="danger"
            >
              <CiDjangoErrorMessages :errors="error" />
            </CiInfoPanel>
            <CiInfoPanel
              :condition="success !== null"
              :text="success"
              type="success"
            />

            <CiButton
              :disabled="loading"
              :class="[loading ? 'button--disabled' : 'button--primary', { hidden: finished }]"
              class="button icon-absolute w-full"
              type="submit"
            >
              <span>{{ i18nGetRegisterButtonLabel }}</span>
            </CiButton>

            <CiButton
              :class="{
                hidden: !finished,
              }"
              class="button button--primary icon-absolute w-full"
              @click="finish"
            >
              <span>{{ $pgettext('Account form button', 'Close') }}</span>
            </CiButton>

            <div
              :class="{ hidden: finished }"
              class="my-4 text-center text-sm"
            >
              <p
                v-html="
                  $gettext(
                    'With the registration I accept the current %{termsLink}. I have read and understood the %{privacyLink}.',
                    { termsLink: i18nTermsOfUseLink, privacyLink: i18PrivacyPolicyLink },
                    true,
                  )
                "
              />
            </div>

            <div
              :class="{ hidden: finished }"
              class="mt-4 text-center text-sm"
            >
              <span>{{ $gettext('Already have an account?') }}</span>&nbsp;
              <nuxt-link :to="localePath({ name: RBN_LOGIN, query: route.query })">
                {{ i18nGetSignInText }}
              </nuxt-link>
            </div>
          </form>
        </div>
      </transition>
    </section>
  </client-only>
</template>

<style></style>
